import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const HISTORY_OPTIONS = [
  'Right Nephrectomy',
  'Left Nephrectomy',
  'Renal Failure',
  'Stones',
  'Hematuria',
  'Pyelonephritis',
  'Reflux'
];

const ECHOTEXTURE_OPTIONS = [
  'Normal',
  'Not Well Visualized',
  'Hypoechoic',
  'Hyperechoic',
  'Atrophy'
];

const HYDRONEPHROSIS_OPTIONS = [
  'None',
  'Pelviectasis',
  'Mild Hydro',
  'Moderate Hydro',
  'Severe Hydro'
];

const KIDNEY_FINDINGS = [
  'Simple Cyst(s)',
  'Stones',
  'Column Bertin',
  'Stent'
];

const BLADDER_FINDINGS = [
  'Foley Catheter',
  'Mass',
  'Debris',
  'Enlarged Prostate'
];

const MASS_ECHOTEXTURE = [
  'Complex Cystic',
  'Solid Hypoechoic',
  'Solid Hyperechoic'
];

// Initial form data structure for Renal form
const INITIAL_RENAL_DATA = {
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: [],
  comments: '',
  rightKidney: {
    nephrectomy: false,
    congenitallyAbsent: false,
    echotexture: 'Normal',
    size1: '',
    size2: '',
    size3: '',
    hydronephrosis: 'None',
    other: [],
    masses: []
  },
  leftKidney: {
    nephrectomy: false,
    congenitallyAbsent: false,
    echotexture: 'Normal',
    size1: '',
    size2: '',
    size3: '',
    hydronephrosis: 'None',
    other: [],
    masses: []
  },
  bladder: {
    nwv: false,
    other: [],
    rightJet: true,
    leftJet: true,
    prevoid: '',
    postvoid: '',
    noVoid: false
  }
};

function RenalForm({ token, onBack, facilityInfo }) {
  // Add activeTab state (0 = Right Kidney, 1 = Left Kidney)
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();
  
  // Use the custom hook
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_RENAL_DATA,
      technologist: defaultTech  // Auto-populate with current user
    },
    'Renal',
    {
      token, facilityInfo
    }
  );

  // Update technologist field when defaultTech loads
  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const calculateVolume = (x, y, z) => {
    if (!x || !y || !z) return '';
    const volume = (parseFloat(x) * parseFloat(y) * parseFloat(z) * 0.52).toFixed(1);
    return volume;
  };

  // Handle history toggle with auto-nephrectomy check
  const handleHistoryChange = useCallback((option) => {
    const newHistory = formData.history.includes(option)
      ? formData.history.filter(h => h !== option)
      : [...formData.history, option];
    
    updateField('history', newHistory);

    // Auto-check nephrectomy if selected in history (and clear the mutually
    // exclusive Congenitally Absent flag, same as the direct checkbox does)
    if (option === 'Right Nephrectomy') {
      const checked = newHistory.includes(option);
      updateField('rightKidney', { ...formData.rightKidney, nephrectomy: checked, congenitallyAbsent: checked ? false : formData.rightKidney.congenitallyAbsent });
    }
    if (option === 'Left Nephrectomy') {
      const checked = newHistory.includes(option);
      updateField('leftKidney', { ...formData.leftKidney, nephrectomy: checked, congenitallyAbsent: checked ? false : formData.leftKidney.congenitallyAbsent });
    }
  }, [formData.history, formData.rightKidney, formData.leftKidney, updateField]);

  // Handle kidney field changes
  const handleKidneyChange = useCallback((side, field, value) => {
    updateNestedField(`${side}.${field}`, value);
  }, [updateNestedField]);

  // Nephrectomy and Congenitally Absent are mutually exclusive (a kidney
  // can't be both surgically removed and never have existed) — built as one
  // merged update since updateNestedField called twice for the same section
  // in one handler would clobber the first change (stale closure).
  const handleKidneyStructuralStatus = useCallback((side, flag, checked) => {
    const other = flag === 'nephrectomy' ? 'congenitallyAbsent' : 'nephrectomy';
    const current = formData[side];
    updateField(side, {
      ...current,
      [flag]: checked,
      [other]: checked ? false : current[other]
    });
  }, [formData, updateField]);

  // Handle kidney findings toggle
  const handleKidneyOtherChange = useCallback((side, finding) => {
    const currentOther = formData[side].other;
    const newOther = currentOther.includes(finding)
      ? currentOther.filter(f => f !== finding)
      : [...currentOther, finding];
    
    updateNestedField(`${side}.other`, newOther);
  }, [formData, updateNestedField]);

  // Add a new kidney mass
  const addKidneyMass = useCallback((side) => {
    const newMass = {
      location: side === 'rightKidney' ? 'Right Kidney' : 'Left Kidney',
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Complex Cystic'
    };
    
    const currentMasses = formData[side].masses;
    updateNestedField(`${side}.masses`, [...currentMasses, newMass]);
  }, [formData, updateNestedField]);

  // Remove a kidney mass
  const removeKidneyMass = useCallback((side, index) => {
    const newMasses = formData[side].masses.filter((_, i) => i !== index);
    updateNestedField(`${side}.masses`, newMasses);
  }, [formData, updateNestedField]);

  // Handle mass field changes
  const handleMassChange = useCallback((side, index, field, value) => {
    const newMasses = formData[side].masses.map((mass, i) => 
      i === index ? { ...mass, [field]: value } : mass
    );
    updateNestedField(`${side}.masses`, newMasses);
  }, [formData, updateNestedField]);

  // Handle bladder findings toggle
  const handleBladderOtherChange = useCallback((finding) => {
    const currentOther = formData.bladder.other;
    const newOther = currentOther.includes(finding)
      ? currentOther.filter(f => f !== finding)
      : [...currentOther, finding];
    
    updateNestedField('bladder.other', newOther);
  }, [formData.bladder.other, updateNestedField]);

  const rightKidneyDisabled = formData.rightKidney.nephrectomy ||
                              formData.rightKidney.congenitallyAbsent ||
                              formData.rightKidney.echotexture === 'Not Well Visualized';
  const leftKidneyDisabled = formData.leftKidney.nephrectomy ||
                             formData.leftKidney.congenitallyAbsent ||
                             formData.leftKidney.echotexture === 'Not Well Visualized';

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Kidney
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Kidney
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Kidney Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Right Kidney</h3>
          
          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={formData.rightKidney.echotexture}
              onChange={(e) => handleKidneyChange('rightKidney', 'echotexture', e.target.value)}
              disabled={formData.rightKidney.nephrectomy || formData.rightKidney.congenitallyAbsent}
            >
              {ECHOTEXTURE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ visibility: 'hidden' }} aria-hidden="true">Echotexture:</label>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'center', height: '38px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.rightKidney.nephrectomy}
                  onChange={(e) => handleKidneyStructuralStatus('rightKidney', 'nephrectomy', e.target.checked)}
                />
                Nephrectomy
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.rightKidney.congenitallyAbsent}
                  onChange={(e) => handleKidneyStructuralStatus('rightKidney', 'congenitallyAbsent', e.target.checked)}
                />
                Congenitally Absent
              </label>
            </div>
          </div>

          {!rightKidneyDisabled && (
            <>
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={formData.rightKidney.size1}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={formData.rightKidney.size2}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={formData.rightKidney.size3}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size3', e.target.value)}
                />
                <span className="volume">
                  = {calculateVolume(formData.rightKidney.size1, formData.rightKidney.size2, formData.rightKidney.size3)} cc
                </span>
              </div>

              <div className="form-group">
                <label>Hydronephrosis:</label>
                <select
                  value={formData.rightKidney.hydronephrosis}
                  onChange={(e) => handleKidneyChange('rightKidney', 'hydronephrosis', e.target.value)}
                >
                  {HYDRONEPHROSIS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group">
                <label>Other Findings:</label>
                {KIDNEY_FINDINGS.map(finding => (
                  <label key={finding}>
                    <input
                      type="checkbox"
                      checked={formData.rightKidney.other.includes(finding)}
                      onChange={() => handleKidneyOtherChange('rightKidney', finding)}
                    />
                    {finding}
                  </label>
                ))}
              </div>

              <button type="button" onClick={() => addKidneyMass('rightKidney')} className="add-mass-btn">
                + Add Right Kidney Mass
              </button>

              {formData.rightKidney.masses.map((mass, index) => (
                <div key={index} className="mass-section">
                  <h4>Mass {index + 1}</h4>
                  <div className="size-inputs">
                    <input
                      type="number"
                      step="0.1"
                      placeholder="L"
                      value={mass.size1}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size1', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="W"
                      value={mass.size2}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size2', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="H"
                      value={mass.size3}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size3', e.target.value)}
                    />
                  </div>
                  <select
                    value={mass.echotexture}
                    onChange={(e) => handleMassChange('rightKidney', index, 'echotexture', e.target.value)}
                  >
                    {MASS_ECHOTEXTURE.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeKidneyMass('rightKidney', index)} className="remove-btn">
                    Remove Mass
                  </button>
                </div>
              ))}
            </>
          )}
              </div>
            )}

            {/* Left Kidney Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Left Kidney</h3>
          
          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={formData.leftKidney.echotexture}
              onChange={(e) => handleKidneyChange('leftKidney', 'echotexture', e.target.value)}
              disabled={formData.leftKidney.nephrectomy || formData.leftKidney.congenitallyAbsent}
            >
              {ECHOTEXTURE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ visibility: 'hidden' }} aria-hidden="true">Echotexture:</label>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '20px', alignItems: 'center', height: '38px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.leftKidney.nephrectomy}
                  onChange={(e) => handleKidneyStructuralStatus('leftKidney', 'nephrectomy', e.target.checked)}
                />
                Nephrectomy
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={formData.leftKidney.congenitallyAbsent}
                  onChange={(e) => handleKidneyStructuralStatus('leftKidney', 'congenitallyAbsent', e.target.checked)}
                />
                Congenitally Absent
              </label>
            </div>
          </div>

          {!leftKidneyDisabled && (
            <>
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={formData.leftKidney.size1}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={formData.leftKidney.size2}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={formData.leftKidney.size3}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size3', e.target.value)}
                />
                <span className="volume">
                  = {calculateVolume(formData.leftKidney.size1, formData.leftKidney.size2, formData.leftKidney.size3)} cc
                </span>
              </div>

              <div className="form-group">
                <label>Hydronephrosis:</label>
                <select
                  value={formData.leftKidney.hydronephrosis}
                  onChange={(e) => handleKidneyChange('leftKidney', 'hydronephrosis', e.target.value)}
                >
                  {HYDRONEPHROSIS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group">
                <label>Other Findings:</label>
                {KIDNEY_FINDINGS.map(finding => (
                  <label key={finding}>
                    <input
                      type="checkbox"
                      checked={formData.leftKidney.other.includes(finding)}
                      onChange={() => handleKidneyOtherChange('leftKidney', finding)}
                    />
                    {finding}
                  </label>
                ))}
              </div>

              <button type="button" onClick={() => addKidneyMass('leftKidney')} className="add-mass-btn">
                + Add Left Kidney Mass
              </button>

              {formData.leftKidney.masses.map((mass, index) => (
                <div key={index} className="mass-section">
                  <h4>Mass {index + 1}</h4>
                  <div className="size-inputs">
                    <input
                      type="number"
                      step="0.1"
                      placeholder="L"
                      value={mass.size1}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size1', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="W"
                      value={mass.size2}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size2', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="H"
                      value={mass.size3}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size3', e.target.value)}
                    />
                  </div>
                  <select
                    value={mass.echotexture}
                    onChange={(e) => handleMassChange('leftKidney', index, 'echotexture', e.target.value)}
                  >
                    {MASS_ECHOTEXTURE.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeKidneyMass('leftKidney', index)} className="remove-btn">
                    Remove Mass
                  </button>
                </div>
              ))}
            </>
          )}
              </div>
            )}
          </div>
        </div>

        {/* Bladder Section */}
        <div className="form-section">
          <h3>Bladder</h3>
          
          <label>
            <input
              type="checkbox"
              checked={formData.bladder.nwv}
              onChange={(e) => updateNestedField('bladder.nwv', e.target.checked)}
            />
            Not Well Visualized
          </label>

          <div className="checkbox-group">
            <label>Findings:</label>
            {BLADDER_FINDINGS.map(finding => (
              <label key={finding}>
                <input
                  type="checkbox"
                  checked={formData.bladder.other.includes(finding)}
                  onChange={() => handleBladderOtherChange(finding)}
                />
                {finding}
              </label>
            ))}
          </div>

          {!formData.bladder.nwv && (
            <>
              <div className="checkbox-group">
                <label>Jets:</label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.bladder.rightJet}
                    onChange={(e) => updateNestedField('bladder.rightJet', e.target.checked)}
                  />
                  Right Jet
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.bladder.leftJet}
                    onChange={(e) => updateNestedField('bladder.leftJet', e.target.checked)}
                  />
                  Left Jet
                </label>
              </div>

              <div className="form-group">
                <label>Pre-void (cc):</label>
                <input
                  type="number"
                  value={formData.bladder.prevoid}
                  onChange={(e) => updateNestedField('bladder.prevoid', e.target.value)}
                />
              </div>

              <label>
                <input
                  type="checkbox"
                  checked={formData.bladder.noVoid}
                  onChange={(e) => updateNestedField('bladder.noVoid', e.target.checked)}
                />
                Patient Unable To Void
              </label>

              {!formData.bladder.noVoid && (
                <div className="form-group">
                  <label>Post-void (PVR) (cc):</label>
                  <input
                    type="number"
                    value={formData.bladder.postvoid}
                    onChange={(e) => updateNestedField('bladder.postvoid', e.target.value)}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default RenalForm;
